import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Job, JobConstants, SalaryType } from "../../Types/Job";

interface JobPostingCreationState {
    isOpen: boolean;
    step: number;
    nextDisabled: boolean;
    type: string | undefined;
    title: string | undefined;
    work_site: string | undefined;
    primary_language: string | undefined;
    pay_amount: {
        pay_type: SalaryType;
        min: string | undefined;
        max: string | undefined;
    };
    pay_rate: string | undefined;
    duration: string | undefined;
    duration_type: string | undefined;
    description: string | undefined;
    skills: string[];
    responsibilities: string[];
    qualifications: string[];
    questions: string[];
    deliverables: string[];
    hasMembership: boolean;
    isDirty: boolean;
    jobLocator: string | undefined;
}

const initialState: JobPostingCreationState = {
    isOpen: false,
    step: 1,
    nextDisabled: true,
    type: undefined,
    title: undefined,
    work_site: undefined,
    primary_language: undefined,
    pay_amount: {
        pay_type: JobConstants.SalaryType.Fixed as SalaryType,
        min: undefined,
        max: undefined
    },
    pay_rate: undefined,
    duration: undefined,
    duration_type: undefined,
    description: undefined,
    skills: [],
    responsibilities: [],
    qualifications: [],
    questions: [],
    deliverables: [],
    hasMembership: false,
    isDirty: false,
    jobLocator: undefined
}

export const jobPostingCreationSlice = createSlice({
    name: 'JobPostingCreation',
    initialState,
    reducers: {
        preloadJobPosting(state, action: PayloadAction<Job>){
            const job = action.payload;
            state.type = job.type;
            state.title = job.title;
            state.work_site = job.work_site;
            state.primary_language = job.primary_language;
            state.pay_amount = {
                pay_type: job.pay_type as SalaryType,
                min: job.pay_amount,
                max: job.pay_amount_max
            };
            state.pay_rate = job.pay_rate;
            state.duration = String(job.duration);
            state.duration_type = job.duration_type;
            state.description = job.description;
            state.skills = job.tags;
            state.responsibilities = job.responsibilities;
            state.qualifications = job.skills.map(s => s.skill);
            state.questions = job.questions?.map(q => q.question) ?? [];
            state.deliverables = job.deliverables ?? [];
            state.isDirty = true;
            state.jobLocator = job.locator;
        },
        setStep(state, action: PayloadAction<JobPostingCreationState["step"]>){
            state.step = action.payload;
        },
        setNextDisabled(state, action: PayloadAction<JobPostingCreationState["nextDisabled"]>){
            state.nextDisabled = action.payload;
        },
        setTitle(state, action: PayloadAction<JobPostingCreationState["title"]>){
            state.title = action.payload;
        },
        setType(state, action: PayloadAction<JobPostingCreationState["type"]>){
            state.type = action.payload;
        },
        setWorkSite(state, action: PayloadAction<JobPostingCreationState["work_site"]>){
            state.work_site = action.payload;
        },
        setPrimaryLanguage(state, action: PayloadAction<JobPostingCreationState["primary_language"]>){
            state.primary_language = action.payload;
        },
        setPayAmount(state, action: PayloadAction<JobPostingCreationState["pay_amount"]>){
            state.pay_amount = action.payload;
        },
        setPayRate(state, action: PayloadAction<JobPostingCreationState["pay_rate"]>){
            state.pay_rate = action.payload;
        },
        setDuration(state, action: PayloadAction<JobPostingCreationState["duration"]>){
            state.duration = action.payload;
        },
        setDurationType(state, action: PayloadAction<JobPostingCreationState["duration_type"]>){
            state.duration_type = action.payload;
        },
        setDescription(state, action: PayloadAction<JobPostingCreationState["description"]>){
            state.description = action.payload;
        },
        setSkills(state, action: PayloadAction<JobPostingCreationState["skills"]>){
            state.skills = action.payload;
        },
        //Responsibilities
        setResponsibilities(state, action: PayloadAction<JobPostingCreationState["responsibilities"]>){
            state.responsibilities = action.payload;
        },
        //Qualifications
        setQualifications(state, action: PayloadAction<JobPostingCreationState["qualifications"]>){
            state.qualifications = action.payload;
        },
        //Questions
        setQuestions(state, action: PayloadAction<JobPostingCreationState["questions"]>){
            state.questions = action.payload;
        },
        //Deliverables
        setDeliverables(state, action: PayloadAction<JobPostingCreationState["deliverables"]>){
            state.deliverables = action.payload;
        },
        setJobPostingModalOpen(state, action: PayloadAction<JobPostingCreationState["isOpen"]>){
            state.isOpen = action.payload
        },
        setHasMembership(state, action: PayloadAction<JobPostingCreationState["hasMembership"]>){
            state.hasMembership = action.payload
        },
        setIsDirty(state, action: PayloadAction<JobPostingCreationState["isDirty"]>){
            state.isDirty = action.payload
        },
        setJobLocator(state, action: PayloadAction<JobPostingCreationState["jobLocator"]>){
            state.jobLocator = action.payload
        },
        reset() {
            return initialState;
        }
    }
})

export const {
    reset,
    setStep,
    setNextDisabled,
    setTitle,
    setType,
    setWorkSite,
    setPrimaryLanguage,
    setPayAmount,
    setPayRate,
    setDuration,
    setDurationType,
    setDescription,
    setSkills,
    setResponsibilities,
    setQualifications,
    setQuestions,
    setDeliverables,
    setJobPostingModalOpen,
    preloadJobPosting,
    setHasMembership,
    setIsDirty,
    setJobLocator
} = jobPostingCreationSlice.actions

export default jobPostingCreationSlice.reducer

